// Libraries
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

// Services
import { apiInstance, gql } from "@/services/httpAdapter";

// Components
import SmartSelector from "../../InputComponents/SmartSelector";
import { LabeledFormInput } from "../LabeledFormInput";
import { ISmartSelectorProps } from "../../InputComponents/SmartSelector/interfaces";

function isValidSelection(value: IState | null) {
	return Boolean(value?.name && value?.code && value?.countryCode && value?.countryName);
}

async function getStatesByCountry(countryId?: number) {
	if (!countryId) {
		return [];
	}

	const query = gql`
		query GetStates($countryId: Int!) {
			getStates(countryId: $countryId) {
				name
				code
				countryCode
				countryName
			}
		}
	`;

	const variables = { countryId };

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.getStates;
	} catch (error) {
		throw new Error("Failed to fetch states");
	}
}

type TStateSelectorProps = {
	countryId?: number;
	onStateSelectedAction?: (state: IState | null) => void;
	inferredState?: IState | null;
	selectorProps?: Partial<ISmartSelectorProps>;
	required?: boolean;
	disabled: boolean;
	loading: boolean;
};

export function StateSelector(props: Readonly<TStateSelectorProps>) {
	const { countryId, onStateSelectedAction, inferredState = null, selectorProps, required = false, disabled, loading } = props;
	const [selectedState, setSelectedState] = useState<IState | null>(null);

	const {
		data: states = [],
		isLoading: isLoadingStates,
		error: errorLoadingStates,
	} = useQuery<IState[]>({
		queryKey: ["getStatesByCountry", countryId],
		queryFn: async () => getStatesByCountry(countryId),
		staleTime: Infinity,
		enabled: Boolean(countryId),
	});

	const handleStateSelection: ISmartSelectorProps["onChangeAction"] = (value) => {
		const state = value as IState;
		setSelectedState(state);
		onStateSelectedAction?.(state);
	};

	useEffect(() => {
		const state = states.find((state: IState) => state.code === inferredState?.code) ?? null;
		setSelectedState(state);
	}, [inferredState, states]);

	return (
		<LabeledFormInput
			label="State/Province"
			formInput={
				<SmartSelector
					selectBy={"code"}
					showBy={"name"}
					options={states}
					selected={selectedState}
					listBoxProps={{}}
					disabled={disabled || (!countryId && !inferredState?.countryCode)}
					sx={{}}
					onChangeAction={handleStateSelection}
					loading={isLoadingStates || loading}
					error={required && (!isValidSelection(selectedState) || Boolean(errorLoadingStates))}
					clearable
					popperProps={selectorProps?.popperProps}
					inputProps={{
						placeholder: "State/Province",
					}}
				/>
			}
		/>
	);
}

export default StateSelector;
